'use client';

import 'dayjs/locale/ko';

import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { notFound, useRouter } from 'next/navigation';
import { useEffect } from 'react';

import { COMMENT_TYPE, UnauthQueryOptions } from '@/api';
import { GtmEventTrigger } from '@/app/_components/GtmEventTrigger';
import { LoadingPageTemplate } from '@/components/client/Templates/LoadingPageTemplate';
import { CommentListWithField } from '@/components/template/CommentListWithField';
import { ROUTES } from '@/constants';
import { CONTENT_CATEGORY, GTM_EVENT_TYPE } from '@/utils/analytics/gtm';
import { getRedirectDetailPage } from '@/utils/redirectDetailPage';

import { PolicyInfoContentBookmarkButton } from '../../_components/PolicyInfoContentBookmarkButton';
import { addAltToImages } from '../_utils/addAltToImages';
import { PolicyInfoDaysAgo } from './PolicyInfoDaysAgo';
import { RelatedPolicyList } from './RelatedPolicyList';

interface PolicyInfoDetailContentProps {
  idIdx: string;
}

/** 클라이언트에서 script 태그 제거 */
const sanitizeHtml = (html: string) => {
  return html.replace(
    /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
    '',
  );
};

/** 에러에서 return_code 추출 */
const getReturnCodeFromError = (error: unknown): number | undefined => {
  if (error && typeof error === 'object' && 'return_code' in error) {
    return (error as { return_code: number }).return_code;
  }

  return undefined;
};

export const PolicyInfoDetailContent = ({
  idIdx,
}: PolicyInfoDetailContentProps) => {
  const router = useRouter();
  const { data, isLoading, error } = useQuery(
    UnauthQueryOptions[
      'get:/wello/v2/allows/contents/best/{bestContentsIdIdx}'
    ]({
      path: { bestContentsIdIdx: idIdx },
    }),
  );

  // 에러 발생 시 return_code로 리다이렉트 판단
  const errorReturnCode = getReturnCodeFromError(error);
  const { shouldRedirect, redirectUrl } = getRedirectDetailPage({
    return_code: errorReturnCode ?? data?.return_code,
    pathname: ROUTES.COMMUNITY_CATEGORY.pathname({ category: 'policyInfo' }),
  });

  useEffect(() => {
    if (redirectUrl) {
      router.replace(redirectUrl);
    }
  }, [redirectUrl, router]);

  if (isLoading || shouldRedirect) {
    return <LoadingPageTemplate />;
  }

  if (error || !data?.context) {
    notFound();
  }

  const context = data.context;
  const id = context.bestContentsId;
  const contents = context.contents;
  const sanitizedContents = contents
    ? addAltToImages(sanitizeHtml(contents), context.title ?? '')
    : '';

  const relatedPolicyList = context.bestContentsPolicyList?.map(
    (relativePolicy) => ({
      id: relativePolicy.metaPolicyId,
      idIdx: relativePolicy.metaPolicyIdIdx,
      title: relativePolicy.policyName,
      agencyLogo: relativePolicy.agencyLogo,
      agency: relativePolicy.agency,
      benefit: relativePolicy.supportBenefit,
      isBookmarked: relativePolicy.wishlistYN,
      dDay: relativePolicy.dday,
      endAt: dayjs(relativePolicy.dueDateEnd)
        .locale('ko')
        .format('YY.MM.DD(ddd)'),
    }),
  );

  return (
    <>
      <header className="flex items-center justify-between gap-8 border-b border-Gray-200 p-16">
        <div className="flex items-center gap-8">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt="웰로 프로필"
            className="size-28 rounded-pill border-1 border-Gray-300"
            src="/assets/pngs/wello-profile.png"
          />
          <div className="font-600 text-Gray-900 typo-body4">웰로</div>
        </div>
        <PolicyInfoDaysAgo fallbackDate={context.daysAgo ?? ''} id={id} />
      </header>
      <section>
        <h1 className="invisible absolute size-0">{context.title}</h1>
        {context.fileUrl && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            alt={context.title ?? ''}
            className="w-full"
            src={context.fileUrl}
          />
        )}
        <div
          className="px-20 pb-30 pt-20 [&_.tableWrapper]:my-16 [&_.tableWrapper]:max-w-full [&_.tableWrapper]:overflow-x-auto [&_.tableWrapper_table]:my-0 [&_:is(td,th)]:whitespace-normal [&_:is(td,th)]:break-words [&_:is(td,th)]:border-1 [&_:is(td,th)]:border-Gray-300 [&_:is(td,th)]:px-12 [&_:is(td,th)]:py-8 [&_:is(td,th)]:align-middle [&_p:empty]:min-h-[1em] [&_table]:w-full [&_table]:max-w-none [&_table]:table-fixed [&_table]:border-collapse [&_th]:bg-Gray-100 [&_th]:text-left [&_th]:font-600"
          dangerouslySetInnerHTML={{
            __html: sanitizedContents,
          }}
        />
      </section>
      <div className="m-20 flex justify-end">
        <PolicyInfoContentBookmarkButton
          id={id}
          idIdx={idIdx}
          title={context.title}
        />
      </div>
      {relatedPolicyList?.length ? (
        <section>
          <h4 className="border-t-8 border-Gray-100 px-20 pt-26 font-600 typo-body1">
            관련 정책을 확인해보세요!
          </h4>
          <RelatedPolicyList relatedPolicyList={relatedPolicyList} />
        </section>
      ) : null}
      <CommentListWithField
        commentType={COMMENT_TYPE.POLICY_INFO}
        contentId={id}
        title={context.title ?? ''}
        policyName={
          relatedPolicyList?.map((policy) => policy.title).join(', ') ||
          undefined
        }
      />
      <GtmEventTrigger
        type={GTM_EVENT_TYPE.VIEW_CONTENT}
        contentName={context.title ?? ''}
        contentCategory={CONTENT_CATEGORY.COMMUNITY}
        contentId={id.toString()}
      />
    </>
  );
};
